import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  getSignRequests,
  deleteSignRequest,
  resendNotification,
} from "../api/signRequests";
import toast from "react-hot-toast";
import TableSkeleton from "../components/TableSkeleton";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import { Tabs, AnimatedModal } from "../components/ui";

const Documents = () => {
  const location = useLocation();
  const { activeCompany } = useAuth();
  const { setHeaderContent } = usePageHeader();
  const [signRequests, setSignRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;
  const pathParts = location.pathname.split("/");

  const subRoute = pathParts[pathParts.length - 1];

  const pillColors = {
    COMPLETED: "bg-green-500 text-white text-xs",
    IN_PROGRESS: "bg-yellow-500 text-white text-xs",
    SENT_EMAIL: "bg-blue-500 text-white text-xs",
    PENDING: "bg-gray-500 text-white text-xs",
    EXPIRED: "bg-red-500 text-white text-xs",
    ARCHIVED: "bg-gray-500 text-white text-xs",
    DECLINED: "bg-red-600 text-white text-xs",
  };

  const statusTextMapped = {
    COMPLETED: "Completed",
    IN_PROGRESS: "In Progress",
    SENT_EMAIL: "Sent Email",
    PENDING: "Pending",
    EXPIRED: "Expired",
    ARCHIVED: "Archived",
    DECLINED: "Declined",
  };

  const fetchSignRequests = async () => {
    try {
      setIsLoading(true);
      const response = await getSignRequests({
        status: subRoute,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
        search: searchQuery || undefined,
      });
      // console.log("response", response);
      setSignRequests(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.totalItems);
      setCurrentPage(response.pagination.currentPage);
    } catch (error) {
      console.error("Error fetching sign requests:", error);
      toast.error(error.message || "Failed to fetch sign requests");
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== undefined) {
        setCurrentPage(1); // Reset to first page when searching
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchSignRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subRoute, currentPage, sortBy, sortOrder, searchQuery]);

  // Set page header (title + description)
  useEffect(() => {
    setHeaderContent(
      <div>
        <BreadCrumb
          items={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Documents" },
          ]}
          className="mb-2"
        />
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
          Documents
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl flex items-center gap-2 mt-1">
          Manage your documents and view details
          {activeCompany && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              {activeCompany.name}
            </span>
          )}
        </p>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [activeCompany, setHeaderContent]);

  const handleDeleteClick = (request) => {
    setSelectedRequest(request);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteSignRequest(selectedRequest._id);
      toast.success("Sign request archived successfully");
      setShowDeleteModal(false);
      setSelectedRequest(null);
      fetchSignRequests(); // Refresh the list
    } catch (error) {
      console.error("Error archiving sign request:", error);
      toast.error(error.message || "Failed to archive sign request");
    }
  };

  const handleResendClick = (request) => {
    setSelectedRequest(request);
    setShowResendModal(true);
  };

  const handleResendConfirm = async () => {
    try {
      await resendNotification(selectedRequest._id);
      toast.success("Notification sent successfully");
      setShowResendModal(false);
      setSelectedRequest(null);
      fetchSignRequests(); // Refresh the list
    } catch (error) {
      console.error("Error resending notification:", error);
      toast.error(error.message || "Failed to resend notification");
    }
  };

  // Called when a sign request is updated from a child component (e.g., email change)
  const handleDocumentUpdated = async (updatedDocument) => {
    // Optimistically update the local list
    setSignRequests((prev) =>
      prev.map((req) =>
        req._id === updatedDocument._id ? updatedDocument : req,
      ),
    );

    // Then refetch from backend to ensure we have the latest data
    try {
      await fetchSignRequests();
    } catch (error) {
      // Errors are already handled inside fetchSignRequests (toast, logging)
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // If clicking the same column, toggle sort order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // If clicking a different column, set it as the new sort column with ascending order
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const tabLinks = [
    { to: "/dashboard/documents", label: "All Documents" },
    { to: "/dashboard/documents/signed", label: "Signed" },
    { to: "/dashboard/documents/pending", label: "Pending" },
    { to: "/dashboard/documents/expired", label: "Expired" },
    { to: "/dashboard/documents/declined", label: "Declined" },
    { to: "/dashboard/documents/archived", label: "Archived" },
  ];

  const searchInput = (
    <div className="px-4 py-5 ">
      <div className="relative w-full">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search documents by name..."
          className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-2xl bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-sm"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <svg
              className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="">
        <Tabs
          tabs={tabLinks.map(({ to, label }) => ({
            value: to,
            label,
            href: to,
          }))}
          value={location.pathname}
          variant="pill"
        />
      </div>
      {/* Table container with search inside */}
      <div className="bg-white overflow-hidden">
        {searchInput}
        {isLoading ? (
          <div className="overflow-hidden">
            <TableSkeleton rows={10} cols={6} showHeader={true} />
          </div>
        ) : (
          <Outlet
            context={{
              signRequests,
              handleDeleteClick,
              handleResendClick,
              pillColors,
              statusTextMapped,
              currentPage,
              totalPages,
              handlePageChange,
              sortBy,
              sortOrder,
              handleSortChange,
              handleDocumentUpdated,
              searchQuery,
            }}
          />
        )}
      </div>

      {/* Resend Notification Confirmation Modal */}
      <AnimatedModal
        isOpen={showResendModal}
        onClose={() => {
          setShowResendModal(false);
          setSelectedRequest(null);
        }}
        closeOnBackdrop
        contentClassName="bg-white shadow-xl overflow-hidden sm:max-w-lg sm:w-full"
      >
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Resend Notification
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to resend notification emails to pending
                  recipients for this document?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            onClick={handleResendConfirm}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow text-base font-medium text-black hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Resend
          </button>
          <button
            type="button"
            onClick={() => {
              setShowResendModal(false);
              setSelectedRequest(null);
            }}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </AnimatedModal>

      {/* Delete Confirmation Modal */}
      <AnimatedModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedRequest(null);
        }}
        closeOnBackdrop
        contentClassName="bg-white shadow-xl overflow-hidden sm:max-w-lg sm:w-full"
      >
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-red-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Archived Document
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to archive this document? This action
                  cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            onClick={handleDeleteConfirm}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedRequest(null);
            }}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </AnimatedModal>
    </div>
  );
};

export default Documents;
